import { useEffect, useState } from "react";
import { adminFetch } from "./adminApi";
import { apiFetchBlob } from "../../data/apiFetch";

/**
 * Diploma / academic-title review.
 * Scans can be opened only while a submission is pending — approving or
 * rejecting wipes them on the server.
 */

const FILTERS = ["SUBMITTED", "APPROVED", "REJECTED", "ALL"];

const DEGREES = { master: "Master", phd: "PhD (кандидат наук / доктор філософії)", doctor: "Doctor of Sciences (доктор наук)" };
const TITLES = { assistant: "Assistant", senior_lecturer: "Senior lecturer", docent: "Docent (доцент)", professor: "Professor" };

const STATUS_COLORS = {
  SUBMITTED: "text-yellow-300 bg-yellow-500/15 border-yellow-400/30",
  APPROVED: "text-green-300 bg-green-500/15 border-green-400/30",
  REJECTED: "text-red-300 bg-red-500/15 border-red-400/30",
};

const selCls = "w-full rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#7269e3]";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4 border-b border-white/5 py-1.5 last:border-0">
      <span className="text-xs text-white/55">{label}</span>
      <span className="text-right text-xs font-semibold text-white/90">{children}</span>
    </div>
  );
}

export default function AdminCredentials() {
  const [filter, setFilter] = useState("SUBMITTED");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null); // { fileId, url, mime }
  const [previewErr, setPreviewErr] = useState("");

  const [degree, setDegree] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/credentials?status=${filter}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Failed to load");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setSelected(null); }, [filter]);

  // Free the object URL whenever the preview changes / page unmounts.
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  function open(item) {
    setSelected(item);
    setPreview(null);
    setPreviewErr("");
    setDegree(item.degree || "");
    setTitle(item.academicTitle || "");
    setNote("");
    setReason("");
    setActionErr("");
  }

  async function showFile(file) {
    setPreviewErr("");
    try {
      const blob = await apiFetchBlob(`/api/admin/credentials/files/${file.id}`);
      setPreview({ fileId: file.id, url: URL.createObjectURL(blob), mime: blob.type || file.mimeType });
    } catch (e) {
      setPreviewErr(e?.status === 410 ? "File already deleted (decision made)." : e?.message || "Failed to open file");
    }
  }

  async function decide(kind) {
    setActionErr("");
    if (kind === "approve" && !degree && !title) return setActionErr("Choose the degree and/or title the documents prove.");
    if (kind === "reject" && reason.trim().length < 3) return setActionErr("Write a reason — the specialist will see it.");
    const msg = kind === "approve"
      ? "Approve? The scans will be deleted right after."
      : "Reject? The specialist gets the reason by email; the scans will be deleted.";
    if (!window.confirm(msg)) return;

    setBusy(true);
    try {
      await adminFetch(`/api/admin/credentials/${selected.id}/${kind}`, {
        method: "POST",
        body: kind === "approve"
          ? { degree: degree || null, academicTitle: title || null, note }
          : { reason, note },
      });
      setSelected(null);
      setPreview(null);
      await load();
    } catch (e) {
      setActionErr(e?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const pending = selected?.status === "SUBMITTED";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Credentials</h2>
          <p className="text-sm text-white/60">Diplomas and academic titles awaiting review.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-lg border px-3 py-1.5 text-xs ${filter === f ? "border-white/30 bg-white/15" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="mb-3 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-200">{err}</div>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* List */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-sm text-white/60">Loading…</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl bg-white/5 p-4 text-sm text-white/60">Nothing here.</div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={() => open(it)}
                className={`w-full rounded-xl border p-3 text-left transition ${selected?.id === it.id ? "border-[#7269e3] bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold">
                    {it.user?.profile?.displayName || it.user?.name || it.user?.email}
                  </span>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] ${STATUS_COLORS[it.status] || ""}`}>{it.status}</span>
                </div>
                <div className="mt-1 text-xs text-white/60">
                  {[DEGREES[it.degree], TITLES[it.academicTitle]].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="mt-1 text-[11px] text-white/45">
                  {fmt(it.submittedAt)} · {it.files?.length || 0} file(s) · ID {it.user?.isVerified ? "✓ verified" : "not verified"}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        {selected ? (
          <div className="space-y-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div>
              <Row label="Name">{selected.user?.profile?.displayName || selected.user?.name || "—"}</Row>
              <Row label="Email">{selected.user?.email}</Row>
              <Row label="Identity (selfie)">{selected.user?.isVerified ? "✅ approved" : selected.user?.verificationStatus}</Row>
              <Row label="Claimed degree">{DEGREES[selected.degree] || "—"}</Row>
              <Row label="Claimed title">{TITLES[selected.academicTitle] || "—"}</Row>
              <Row label="Institution">{selected.institution || "—"}</Row>
              <Row label="Consent given">{fmt(selected.consentAt)}</Row>
              <Row label="Submitted">{fmt(selected.submittedAt)}</Row>
              {selected.reviewedAt && <Row label="Reviewed">{fmt(selected.reviewedAt)} · {selected.reviewedBy}</Row>}
              {selected.status === "APPROVED" && (
                <Row label="Approved">{[DEGREES[selected.approvedDegree], TITLES[selected.approvedTitle]].filter(Boolean).join(" · ")}</Row>
              )}
              {selected.rejectionReason && <Row label="Rejection reason">{selected.rejectionReason}</Row>}
              {selected.reviewNote && <Row label="Internal note">{selected.reviewNote}</Row>}
            </div>

            {/* Files */}
            <div>
              <div className="mb-2 text-xs font-semibold text-white/70">Documents</div>
              {selected.filesDeletedAt ? (
                <div className="text-xs text-white/50">Scans deleted {fmt(selected.filesDeletedAt)}.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selected.files?.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => showFile(f)}
                      className={`rounded-lg border px-3 py-1.5 text-xs ${preview?.fileId === f.id ? "border-[#7269e3] bg-white/15" : "border-white/15 bg-white/5 hover:bg-white/10"}`}
                    >
                      📄 {f.originalName || f.mimeType} · {Math.round(f.size / 1024)} KB
                    </button>
                  ))}
                </div>
              )}
              {previewErr && <div className="mt-2 text-xs text-red-300">{previewErr}</div>}
              {preview && (
                <div className="mt-3">
                  {preview.mime === "application/pdf" ? (
                    <iframe title="document" src={preview.url} className="h-[520px] w-full rounded-lg bg-white" />
                  ) : (
                    <img src={preview.url} alt="document" className="max-h-[520px] w-full rounded-lg object-contain bg-black/30" />
                  )}
                  <a href={preview.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[#b9b3ff] underline">
                    Open in new tab
                  </a>
                </div>
              )}
            </div>

            {pending && (
              <>
                <div className="rounded-lg bg-white/5 p-3 text-xs text-white/60">
                  Check: name matches the identity verification; document number in the state education registry (ЄДЕБО) or the
                  university / НАЗЯВО records; title certificate (атестат доцента/професора). Write the source in the note.
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-white/70">Confirmed degree</label>
                    <select value={degree} onChange={(e) => setDegree(e.target.value)} className={selCls}>
                      <option value="">— not confirmed —</option>
                      {Object.entries(DEGREES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-white/70">Confirmed title</label>
                    <select value={title} onChange={(e) => setTitle(e.target.value)} className={selCls}>
                      <option value="">— not confirmed —</option>
                      {Object.entries(TITLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs text-white/70">Internal note (source checked) — not shown to the specialist</label>
                  <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={selCls} placeholder="e.g. ЄДЕБО, diploma № … found" />
                </div>

                <div>
                  <label className="mb-1 block text-xs text-white/70">Rejection reason (emailed to the specialist)</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2} className={selCls} placeholder="e.g. The scan is unreadable — please upload a clearer photo" />
                </div>

                {actionErr && <div className="rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-200">{actionErr}</div>}

                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy}
                    onClick={() => decide("approve")}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
                  >
                    ✅ Approve
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => decide("reject")}
                    className="rounded-lg bg-red-600/80 px-4 py-2 text-sm font-medium hover:bg-red-500 disabled:opacity-50"
                  >
                    ❌ Reject
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="hidden rounded-xl border border-dashed border-white/10 p-6 text-sm text-white/50 lg:block">
            Select a submission to review.
          </div>
        )}
      </div>
    </div>
  );
}
